import React, { useState, useCallback } from 'react';
import {
  Page,
  Layout,
  Card,
  BlockStack,
  InlineStack,
  Text,
  Avatar,
  Badge,
  Button,
  Box,
  Collapsible,
  Modal,
  Divider,
  InlineGrid,
  Tag,
} from '@shopify/polaris';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  PersonIcon,
  OrderIcon,
  MoneyIcon,
  CalendarIcon,
  PhoneIcon,
  EmailIcon,
  LocationIcon,
} from '@shopify/polaris-icons';
import { Helmet } from 'react-helmet-async';

// --- Types ---
interface AffiliateMember {
  id: string;
  name: string;
  email: string;
  phone: string;
  avatar: string;
  rank: 'Iron' | 'Bronze' | 'Silver' | 'Gold' | 'Diamond';
  joinDate: string;
  location: string;
  stats: {
    totalSales: number;
    totalOrders: number;
    commission: number;
    thisMonthIncome: number;
  };
  children?: AffiliateMember[];
}

// --- Mock Data ---
const MOCK_SYSTEM_DATA: AffiliateMember[] = [
  {
    id: 'USER-001',
    name: 'Lê Văn B',
    email: 'levanb@example.com',
    phone: '0909123456',
    avatar: 'https://i.pravatar.cc/150?u=USER-001',
    rank: 'Gold',
    joinDate: '2023-06-01',
    location: 'Hồ Chí Minh',
    stats: { totalSales: 150000000, totalOrders: 120, commission: 15000000, thisMonthIncome: 2500000 },
    children: [
      {
        id: 'USER-002',
        name: 'Trần Thị C',
        email: 'tranthic@example.com',
        phone: '0909123457',
        avatar: 'https://i.pravatar.cc/150?u=USER-002',
        rank: 'Silver',
        joinDate: '2023-07-15',
        location: 'Hà Nội',
        stats: { totalSales: 80000000, totalOrders: 65, commission: 8000000, thisMonthIncome: 1200000 },
        children: [
          {
            id: 'USER-004',
            name: 'Phạm Văn E',
            email: 'phamvane@example.com',
            phone: '0909123459',
            avatar: 'https://i.pravatar.cc/150?u=USER-004',
            rank: 'Iron',
            joinDate: '2023-09-10',
            location: 'Đà Nẵng',
            stats: { totalSales: 20000000, totalOrders: 15, commission: 2000000, thisMonthIncome: 500000 },
          },
          {
            id: 'USER-005',
            name: 'Ngô Thị F',
            email: 'ngothif@example.com',
            phone: '0909123460',
            avatar: 'https://i.pravatar.cc/150?u=USER-005',
            rank: 'Iron',
            joinDate: '2023-09-12',
            location: 'Cần Thơ',
            stats: { totalSales: 15000000, totalOrders: 10, commission: 1500000, thisMonthIncome: 300000 },
          },
        ],
      },
      {
        id: 'USER-003',
        name: 'Hoàng Văn D',
        email: 'hoangvand@example.com',
        phone: '0909123458',
        avatar: 'https://i.pravatar.cc/150?u=USER-003',
        rank: 'Bronze',
        joinDate: '2023-08-05',
        location: 'Đồng Nai',
        stats: { totalSales: 45000000, totalOrders: 35, commission: 4500000, thisMonthIncome: 800000 },
      },
    ],
  },
  {
    id: 'USER-006',
    name: 'Nguyễn Thị G',
    email: 'nguyenthig@example.com',
    phone: '0909123461',
    avatar: 'https://i.pravatar.cc/150?u=USER-006',
    rank: 'Diamond',
    joinDate: '2023-05-20',
    location: 'Hồ Chí Minh',
    stats: { totalSales: 350000000, totalOrders: 280, commission: 35000000, thisMonthIncome: 5500000 },
    children: [
      {
        id: 'USER-007',
        name: 'Lý Văn H',
        email: 'lyvanh@example.com',
        phone: '0909123462',
        avatar: 'https://i.pravatar.cc/150?u=USER-007',
        rank: 'Gold',
        joinDate: '2023-06-15',
        location: 'Bình Dương',
        stats: { totalSales: 120000000, totalOrders: 95, commission: 12000000, thisMonthIncome: 2100000 },
      },
    ],
  },
];

// --- Helpers ---
const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(amount);
};

const getRankColor = (rank: string) => {
  switch (rank) {
    case 'Diamond':
      return 'info';
    case 'Gold':
      return 'warning';
    case 'Silver':
      return 'success'; // Using success as silver-ish placeholder or specific styling
    case 'Bronze':
      return 'attention';
    default:
      return 'new';
  }
};

// --- Components ---

const MemberDetailModal = ({ member, open, onClose }: { member: AffiliateMember | null; open: boolean; onClose: () => void }) => {
  if (!member) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Thông tin thành viên"
      primaryAction={{
        content: 'Đóng',
        onAction: onClose,
      }}
    >
      <Modal.Section>
        <BlockStack gap="500">
          {/* Header Info */}
          <InlineStack align="space-between" blockAlign="start">
            <InlineStack gap="400">
              <Avatar size="xl" source={member.avatar} name={member.name} />
              <BlockStack gap="100">
                <Text variant="headingLg" as="h2">
                  {member.name}
                </Text>
                <InlineStack gap="200">
                  <Badge tone={getRankColor(member.rank)}>{`${member.rank} Partner`}</Badge>
                  <Text variant="bodySm" as="span" tone="subdued">
                    ID: {member.id}
                  </Text>
                </InlineStack>
              </BlockStack>
            </InlineStack>
          </InlineStack>

          <Divider />

          {/* Contact Info */}
          <BlockStack gap="300">
            <Text variant="headingSm" as="h3">
              Thông tin liên hệ
            </Text>
            <InlineGrid columns={{ xs: 1, sm: 2 }} gap="300">
              <InlineStack gap="200">
                <EmailIcon width={20} color="subdued" />
                <Text variant="bodyMd" as="span">
                  {member.email}
                </Text>
              </InlineStack>
              <InlineStack gap="200">
                <PhoneIcon width={20} color="subdued" />
                <Text variant="bodyMd" as="span">
                  {member.phone}
                </Text>
              </InlineStack>
              <InlineStack gap="200">
                <LocationIcon width={20} color="subdued" />
                <Text variant="bodyMd" as="span">
                  {member.location}
                </Text>
              </InlineStack>
              <InlineStack gap="200">
                <CalendarIcon width={20} color="subdued" />
                <Text variant="bodyMd" as="span">
                  Tham gia: {member.joinDate}
                </Text>
              </InlineStack>
            </InlineGrid>
          </BlockStack>

          <Divider />

          {/* Stats */}
          <BlockStack gap="300">
            <Text variant="headingSm" as="h3">
              Hiệu quả kinh doanh
            </Text>
            <InlineGrid columns={{ xs: 2, sm: 2 }} gap="400">
              <Box padding="300" background="bg-surface-secondary" borderRadius="200">
                <BlockStack gap="100">
                  <InlineStack gap="200">
                    <MoneyIcon width={20} color="#00a0ac" />
                    <Text variant="bodySm" as="span" tone="subdued">
                      Doanh số tổng
                    </Text>
                  </InlineStack>
                  <Text variant="headingMd" as="p">
                    {formatCurrency(member.stats.totalSales)}
                  </Text>
                </BlockStack>
              </Box>
              <Box padding="300" background="bg-surface-secondary" borderRadius="200">
                <BlockStack gap="100">
                  <InlineStack gap="200">
                    <OrderIcon width={20} color="#5c5f62" />
                    <Text variant="bodySm" as="span" tone="subdued">
                      Tổng đơn hàng
                    </Text>
                  </InlineStack>
                  <Text variant="headingMd" as="p">
                    {member.stats.totalOrders}
                  </Text>
                </BlockStack>
              </Box>
              <Box padding="300" background="bg-surface-secondary" borderRadius="200">
                <BlockStack gap="100">
                  <InlineStack gap="200">
                    <MoneyIcon width={20} color="#e49400" />
                    <Text variant="bodySm" as="span" tone="subdued">
                      Hoa hồng tích lũy
                    </Text>
                  </InlineStack>
                  <Text variant="headingMd" as="p">
                    {formatCurrency(member.stats.commission)}
                  </Text>
                </BlockStack>
              </Box>
              <Box padding="300" background="bg-surface-secondary" borderRadius="200">
                <BlockStack gap="100">
                  <InlineStack gap="200">
                    <MoneyIcon width={20} color="#008060" />
                    <Text variant="bodySm" as="span" tone="subdued">
                      Thu nhập tháng này
                    </Text>
                  </InlineStack>
                  <Text variant="headingMd" as="p">
                    {formatCurrency(member.stats.thisMonthIncome)}
                  </Text>
                </BlockStack>
              </Box>
            </InlineGrid>
          </BlockStack>
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
};

const MemberNode = ({ member, level, onSelect }: { member: AffiliateMember; level: number; onSelect: (member: AffiliateMember) => void }) => {
  const [open, setOpen] = useState(level < 1); // Auto expand first level
  const hasChildren = member.children && member.children.length > 0;
  const maxLevel = 3;

  const handleToggle = useCallback(() => setOpen((prev) => !prev), []);

  return (
    <Box paddingBlockStart="200">
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '12px',
          backgroundColor: 'var(--p-color-bg-surface)',
          border: '1px solid var(--p-color-border)',
          borderRadius: '8px',
          marginLeft: `${level * 24}px`,
          cursor: 'pointer',
          position: 'relative',
        }}
        onClick={() => onSelect(member)}
      >
        {/* Connector Line for nested levels */}
        {level > 0 && (
          <div
            style={{
              position: 'absolute',
              left: '-18px',
              top: '50%',
              width: '18px',
              height: '1px',
              backgroundColor: 'var(--p-color-border)',
            }}
          />
        )}

        <InlineStack gap="300" align="start" blockAlign="center">
          {/* Expand/Collapse Button */}
          <div
            onClick={(e) => {
              e.stopPropagation();
              if (hasChildren) handleToggle();
            }}
            style={{ cursor: hasChildren ? 'pointer' : 'default', opacity: hasChildren ? 1 : 0.3, display: 'flex' }}
          >
            {open ? <ChevronUpIcon width={20} /> : <ChevronDownIcon width={20} />}
          </div>

          <Avatar customer size="md" source={member.avatar} name={member.name} />

          <div style={{ flex: 1 }}>
            <InlineStack align="space-between" blockAlign="center">
              <BlockStack gap="050">
                <Text variant="bodyMd" fontWeight="bold" as="span">
                  {member.name}
                </Text>
                <InlineStack gap="200">
                  <Badge size="small" tone={getRankColor(member.rank)}>
                    {member.rank}
                  </Badge>
                  <Text variant="bodySm" tone="subdued" as="span">
                    {member.children?.length || 0} cấp dưới
                  </Text>
                </InlineStack>
              </BlockStack>

              {/* Mini Stats - Hide on very small screens if needed */}
              <Box paddingInlineStart="400">
                <Text variant="bodySm" fontWeight="bold" as="span">
                  {formatCurrency(member.stats.totalSales)}
                </Text>
              </Box>
            </InlineStack>
          </div>
        </InlineStack>
      </div>

      {/* Children */}
      <Collapsible open={open && level < maxLevel} id={`collapse-${member.id}`} transition={{ duration: '500ms', timingFunction: 'ease-in-out' }}>
        {hasChildren && (
          <div style={{ position: 'relative' }}>
            {/* Vertical connector line */}
            <div
              style={{
                position: 'absolute',
                left: `${level * 24 + 12}px`,
                top: '0',
                bottom: '20px',
                width: '1px',
                backgroundColor: 'var(--p-color-border)',
                zIndex: 0,
              }}
            />
            {member.children!.map((child) => (
              <MemberNode key={child.id} member={child} level={level + 1} onSelect={onSelect} />
            ))}
          </div>
        )}
      </Collapsible>
    </Box>
  );
};

export default function AgencySystem() {
  const [selectedMember, setSelectedMember] = useState<AffiliateMember | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const handleMemberClick = useCallback((member: AffiliateMember) => {
    setSelectedMember(member);
    setModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    setSelectedMember(null);
  }, []);

  return (
    <Page
      title="Hệ thống thành viên"
      subtitle="Quản lý và theo dõi mạng lưới affiliate của bạn (Tối đa 3 cấp)"
      fullWidth
      primaryAction={<Button icon={PersonIcon}>Thêm thành viên</Button>}
    >
      <Helmet>
        <title>Hệ thống thành viên - Affiliate Network</title>
      </Helmet>

      <Layout>
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <BlockStack gap="200">
                <Text variant="headingMd" as="h2">
                  Cây hệ thống
                </Text>
                <Text variant="bodySm" tone="subdued" as="p">
                  Hiển thị danh sách các thành viên trong mạng lưới của bạn. Nhấn vào tên thành viên để xem chi tiết.
                </Text>
              </BlockStack>

              <Box paddingBlockEnd="400">
                {MOCK_SYSTEM_DATA.map((member) => (
                  <MemberNode key={member.id} member={member} level={0} onSelect={handleMemberClick} />
                ))}
              </Box>
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>

      <MemberDetailModal member={selectedMember} open={modalOpen} onClose={handleCloseModal} />
    </Page>
  );
}

